/**
 * How wide each column of a proposal's document collection wants to be.
 *
 * Conflicts and Policies draw `Document | Type | Added by | Added` beside a PINNED row-actions column,
 * inside a panel the tab splits with its templates panel. Four content columns plus the pinned one come
 * to more than that panel gives them, so the table is wider than its container and the pinned controls
 * float over whatever has scrolled under them — which at a 1600px viewport was the tail of the Added
 * column, reading "09/16/26 0" on the draft's Conflicts and "09/16/2" on the active page's (#4955).
 *
 * **All four columns are drawn, and the table overflows.** That is what the Fee Schedule beside them
 * does — it keeps Name, Description, Fee and Team Member, and lets Team Member run under its own pinned
 * controls, with the table's own scrollbar as the way to reach it. The owner named
 * these four columns, and a Conflicts tab showing only a file name and a date does not read as the same
 * table; it reads as a list with a date on it. An earlier pass here shed Type and Added by at 1600px to
 * make everything fit, which answered a question nobody had asked: a column reachable by scrolling to
 * it is fine, a column silently removed is not.
 *
 * So the fit does two things, and dropping a column is not one of them:
 *
 * 1. It floors every column at a width where the column is worth drawing at all. `added`'s floor is the
 *    reason this file exists: measured from the STAMP rather than from the header, so that a date the
 *    reader scrolls to is whole rather than cut mid-stamp. That is what "09/16/2" was.
 * 2. Where the panel is wider than those floors — the templates panel collapsed, a wide display, the
 *    Client Team tab's full width — it shares the surplus among the columns that can use it, so the
 *    columns fill the panel instead of leaving dead space in front of the pinned controls.
 *
 * The table is laid out against these widths in MRT's `grid` mode, which is what gives the `nowrap`
 * truncation the cells already ask for something to truncate against — and what lets a reader drag a
 * column wider, or double-click its header open, when the truncation is not what they want (#4955).
 *
 * The keys are the column ids `document-collection-table` declares. They are written as literals here,
 * the way the fee schedule's are, so that this file stays a table of numbers with no import back into
 * the module that imports it.
 */

/**
 * A column's preferred width, and the narrowest it can be and still be worth drawing.
 *
 * A minimum is the width of the *header*, not of the values underneath it: a heading now carries a sort
 * control and a column menu beside its text, so a column too narrow to name itself is unreadable — the
 * reader cannot tell what they are looking at, and the values ellipsize into the measured tooltip
 * `feeScheduleTableCellStyles` already gives them either way.
 *
 * Measured against Roboto at the 14px both the head and body cells use, with MUI's body2 letter
 * spacing: a header needs its text, plus the sort label MRT sizes at `3ch` (23.6px), plus the column
 * menu button (a 2rem button with a `-4px` side margin, so 24px of flow), plus the comfortable head
 * cell's own `1rem` padding on each side. `Added` is the exception, and the reason for this change —
 * see below.
 */
export type DocumentCollectionColumnWidth = { preferred: number; minimum: number };

export const DOCUMENT_COLLECTION_COLUMN_WIDTHS: Readonly<Record<string, DocumentCollectionColumnWidth>> = {
  /** A single lock glyph, centred. It has no header text to fit and nothing to gain from more room. */
  locked: { preferred: 40, minimum: 40 },
  /**
   * The row's identity, and the only genuinely elastic column here — a file name is as long as someone
   * made it. "Document" needs 65.6 + 23.6 + 24 + 32 = 145.2px to name itself.
   */
  document: { preferred: 320, minimum: 146 },
  /**
   * "Type" needs 31.8 + 23.6 + 24 + 32 = 111.4px to name itself, while its widest value ("DOCX") needs
   * 69px — so the header binds, and room above it would be spent on nothing.
   */
  type: { preferred: 112, minimum: 112 },
  /** "Added by" needs 60.2 + 23.6 + 24 + 32 = 139.8px; a full name routinely wants more than that. */
  addedBy: { preferred: 200, minimum: 140 },
  /**
   * The one column whose minimum is measured from its VALUES rather than from its header, because the
   * value is what #4955 was about. A full `MM/DD/YY hh:mm A` is 124.5px of Roboto at the body cell's
   * 14px (the widest of the stamps: Roboto's digits are tabular, so only the AM/PM and the letters
   * vary), and the comfortable body cell spends 32px of the column on its own `1rem` padding — 156.5px,
   * rounded up to 157. Its header would settle for 121, which is exactly how the column came to be
   * drawn too narrow to show a date.
   *
   * Preferred is the same number: a stamp is fixed-width content, so a wider Added column shows nothing
   * more. Any surplus goes to Document and Added by instead.
   */
  added: { preferred: 157, minimum: 157 },
};

/**
 * What the trailing controls column asks for, which is not the same on the two surfaces.
 *
 * A DRAFT row offers three controls: Download and Remove at `theme.spacing(0.5)` of padding around a
 * 24px icon (32px each), and the field-history dropdown at MUI's own medium icon-button size (40px).
 * 104px of buttons inside the comfortable display cell's `0.5rem 0.75rem` padding — 128px.
 *
 * An ACTIVE row offers five: Replace and Remove (or, once something is pending, Undo), Accept, Reject,
 * and the history dropdown. 4 × 32 + 40 = 168px inside the same 24px of padding — 192px.
 *
 * Sized for the most controls a row can offer rather than for the most this row happens to offer: the
 * column is one width for the whole table, and a column sized to a three-button row would wrap the
 * buttons on a five-button one. Preferred equals minimum for the same reason `added`'s does — the
 * buttons are a fixed size and a wider column only pads them.
 */
/** A row control: `theme.spacing(0.5)` of padding around a 24px icon. */
const ROW_ACTION_BUTTON_WIDTH = 32;
/** The field-history control, which is MUI's medium icon button rather than the compact one. */
const ROW_ACTION_MENU_WIDTH = 40;
/** The comfortable display cell's own `0.5rem 0.75rem`. */
const ROW_ACTIONS_CELL_PADDING = 24;
/** "Actions" needs 60.2px of Roboto at the head cell's 14px, inside that same padding. */
const ROW_ACTIONS_HEADER_WIDTH = 85;

/**
 * What the controls column needs for a row offering `buttons` controls, beside the field-history
 * control every row carries.
 *
 * Pass the widest row's count, not a row's: the column is one width for the whole table, and a column
 * sized to a two-control row would wrap the controls on a four-control one. What it should NOT be
 * sized to is the most controls the surface can produce — a collection whose rows all happen to offer
 * one control reserved 192px of a 382px phone table for 96px of buttons. The floor is the header's own
 * word, so a collection with no rows at all still reads "Actions".
 */
export function rowActionsColumnWidth(buttons: number): number {
  const controls = buttons * ROW_ACTION_BUTTON_WIDTH + ROW_ACTION_MENU_WIDTH;
  return Math.max(ROW_ACTIONS_HEADER_WIDTH, controls + ROW_ACTIONS_CELL_PADDING);
}

/** Download, Remove and the history dropdown. */
export const DRAFT_ROW_ACTIONS_COLUMN_WIDTH = rowActionsColumnWidth(2);
/** Replace (or Undo), Remove, Accept, Reject and the history dropdown — the most an active row offers. */
export const ACTIVE_ROW_ACTIONS_COLUMN_WIDTH = rowActionsColumnWidth(4);

/** What a column asks for when it is not one of the ones above — MRT's own default column width. */
const UNKNOWN_COLUMN_WIDTH: DocumentCollectionColumnWidth = { preferred: 180, minimum: 140 };

const ROW_ACTIONS_COLUMN = "mrt-row-actions";

export function documentCollectionColumnWidth(
  columnId: string,
  rowActionsWidth: number,
): DocumentCollectionColumnWidth {
  if (columnId === ROW_ACTIONS_COLUMN) return { preferred: rowActionsWidth, minimum: rowActionsWidth };
  return DOCUMENT_COLLECTION_COLUMN_WIDTHS[columnId] ?? UNKNOWN_COLUMN_WIDTH;
}

export type DocumentCollectionColumnFit = {
  /** What each column should be given, in pixels. Every column the collection declares gets one. */
  widths: Record<string, number>;
};

const NOTHING_SIZED: DocumentCollectionColumnFit = { widths: {} };

/**
 * Fits a document collection's columns to the width the collection has been given.
 *
 * Every column stays. Each is floored at the width below which it stops being readable, and any
 * surplus above those floors is shared among the columns that can use it, in proportion to how much
 * more than its floor each one asked for — so a file name gets the room a date stamp has no use for,
 * and the columns reach the pinned controls instead of stopping short of them.
 *
 * Where the floors come to more than the panel — which is the usual case on a tab that splits its width
 * with a templates panel — every column sits on its floor and the table is wider than its container.
 * It then scrolls, behind the scrollbar `MRTNoMemo` draws on every table container, which is what the
 * Fee Schedule does with its own Team Member column. Nothing is hidden to avoid that: the reader can
 * see a column is there and reach it, which is the whole difference between a table that overflows and
 * one that has quietly dropped half of itself.
 *
 * An unmeasured width sizes nothing, leaving the collection exactly as it was before it was measured —
 * the first render, and any renderer without `ResizeObserver`.
 */
export function fitDocumentCollectionColumns(
  availableWidth: number | undefined,
  presentColumnIds: readonly string[],
  rowActionsWidth: number,
): DocumentCollectionColumnFit {
  if (availableWidth === undefined) return NOTHING_SIZED;

  const columns = presentColumnIds.map((columnId) => ({
    columnId,
    ...documentCollectionColumnWidth(columnId, rowActionsWidth),
  }));

  const totalMinimum = columns.reduce((total, column) => total + column.minimum, 0);
  const totalWanted = columns.reduce(
    (total, column) => total + Math.max(0, column.preferred - column.minimum),
    0,
  );
  const surplus = Math.max(0, availableWidth - totalMinimum);

  const widths: Record<string, number> = {};
  for (const column of columns) {
    const wanted = Math.max(0, column.preferred - column.minimum);
    // A surplus with nothing asking for it is spread evenly rather than dropped on the floor, so the
    // columns still reach the edge of a panel wider than every preferred width put together.
    const share = totalWanted === 0 ? surplus / columns.length : (wanted / totalWanted) * surplus;
    widths[column.columnId] = Math.floor(column.minimum + share);
  }
  return { widths };
}
